import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/** Default cadence for background refreshes of a table view: 5 minutes. */
export const DEFAULT_AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export interface AutoRefreshRegistration {
  /** Reloads the view's data in place, without touching search or pagination */
  onRefresh: () => void;
  /** Cadence to refresh at, defaults to DEFAULT_AUTO_REFRESH_INTERVAL_MS */
  intervalMs?: number;
}

export interface AutoRefreshState {
  /** True while a view that supports in-place refreshing is mounted */
  isActive: boolean;
  /** Epoch ms of the last successful load, null before the first one lands */
  lastRefreshedAt: number | null;
  /** Epoch ms the next background refresh is due, null while inactive */
  nextRefreshAt: number | null;
  /** True while a load is in flight */
  isRefreshing: boolean;
  /** Cadence the active view refreshes at */
  intervalMs: number;
  /** Reload the active view's data now; a no-op when nothing is registered */
  refreshNow: () => void;
  /** Register the active view's refresh handler. Returns an unregister fn. */
  registerAutoRefresh: (registration: AutoRefreshRegistration) => () => void;
  /** Report that a load finished, which restarts the countdown */
  markRefreshed: (succeeded?: boolean) => void;
  /** Report whether a load is currently in flight */
  setRefreshing: (refreshing: boolean) => void;
}

const AutoRefreshContext = createContext<AutoRefreshState | null>(null);

// Returned when no provider is above the consumer: nothing is registered, so
// the app bar falls back to a full page reload instead of an in-place refresh.
const INERT_STATE: AutoRefreshState = {
  isActive: false,
  lastRefreshedAt: null,
  nextRefreshAt: null,
  isRefreshing: false,
  intervalMs: DEFAULT_AUTO_REFRESH_INTERVAL_MS,
  refreshNow: () => {},
  registerAutoRefresh: () => () => {},
  markRefreshed: () => {},
  setRefreshing: () => {},
};

export const useAutoRefresh = (): AutoRefreshState => {
  const state = useContext(AutoRefreshContext);
  if (state) {
    return state;
  }
  // Outside a provider: shout in dev, degrade quietly in production. The check
  // has to live in the hook — as a `createContext` default argument it ran at
  // module scope, so importing this file threw before any component rendered.
  if (process.env.NODE_ENV !== "production") {
    throw new Error("useAutoRefresh must be used within AutoRefreshProvider");
  }
  return INERT_STATE;
};

export const AutoRefreshProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const handlerRef = useRef<(() => void) | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [intervalMs, setIntervalMs] = useState(
    DEFAULT_AUTO_REFRESH_INTERVAL_MS,
  );
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  // Tracks every completed load, successful or not, so a failed refresh still
  // schedules the next attempt instead of stalling the countdown.
  const [lastAttemptAt, setLastAttemptAt] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Keep this ref in sync with lastAttemptAt so the visibility listener can
  // recalculate dueAt without a stale closure.
  const lastAttemptAtRef = useRef(lastAttemptAt);
  lastAttemptAtRef.current = lastAttemptAt;

  const registerAutoRefresh = useCallback(
    ({
      onRefresh,
      intervalMs: registeredInterval,
    }: AutoRefreshRegistration) => {
      handlerRef.current = onRefresh;
      setIntervalMs(registeredInterval ?? DEFAULT_AUTO_REFRESH_INTERVAL_MS);
      setIsActive(true);

      return () => {
        if (handlerRef.current === onRefresh) {
          handlerRef.current = null;
        }
        setIsActive(false);
        setIsRefreshing(false);
        setLastRefreshedAt(null);
        setLastAttemptAt(null);
      };
    },
    [],
  );

  const markRefreshed = useCallback((succeeded = true) => {
    const now = Date.now();
    setLastAttemptAt(now);
    if (succeeded) {
      setLastRefreshedAt(now);
    }
  }, []);

  const setRefreshing = useCallback((refreshing: boolean) => {
    setIsRefreshing(refreshing);
  }, []);

  const refreshNow = useCallback(() => {
    handlerRef.current?.();
  }, []);

  // Schedule the next background refresh, counting from the last completed
  // load so that a search or page change also pushes the countdown out.
  useEffect(() => {
    if (!isActive || lastAttemptAt === null) {
      return undefined;
    }

    const dueAt = lastAttemptAt + intervalMs;

    // Advancing the countdown here rather than waiting for the view to report
    // back keeps the loop alive even when a refresh is skipped (for instance
    // because a load is already running).
    const refresh = () => {
      setLastAttemptAt(Date.now());
      handlerRef.current?.();
    };

    // Skip refreshes while the tab is in the background; the visibility
    // listener below catches up as soon as the user comes back.
    const timeoutId = setTimeout(
      () => {
        if (document.hidden) {
          return;
        }
        refresh();
      },
      Math.max(0, dueAt - Date.now()),
    );

    // Recalculate dueAt inside the listener from the current state value
    // (via the ref) instead of using the stale closure snapshot.
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const currentDueAt = (lastAttemptAtRef.current ?? 0) + intervalMs;
        if (Date.now() >= currentDueAt) {
          refresh();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isActive, intervalMs, lastAttemptAt]);

  return (
    <AutoRefreshContext.Provider
      value={{
        isActive,
        lastRefreshedAt,
        nextRefreshAt:
          isActive && lastAttemptAt !== null
            ? lastAttemptAt + intervalMs
            : null,
        isRefreshing,
        intervalMs,
        refreshNow,
        registerAutoRefresh,
        markRefreshed,
        setRefreshing,
      }}
    >
      {children}
    </AutoRefreshContext.Provider>
  );
};

export interface UseRegisterAutoRefreshOptions extends AutoRefreshRegistration {
  /** Whether this view should auto-refresh at all */
  enabled?: boolean;
  /** Whether a load is currently in flight */
  isRefreshing?: boolean;
}

/**
 * Registers the calling view as the one the app bar refresh control drives,
 * and starts the background refresh countdown.
 *
 * `onRefresh` must be referentially stable (wrap it in `useCallback`).
 * Call the returned `markRefreshed` whenever a load settles.
 */
export const useRegisterAutoRefresh = ({
  onRefresh,
  intervalMs,
  enabled = true,
  isRefreshing = false,
}: UseRegisterAutoRefreshOptions) => {
  const { registerAutoRefresh, markRefreshed, setRefreshing } =
    useAutoRefresh();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return registerAutoRefresh({ onRefresh, intervalMs });
  }, [enabled, onRefresh, intervalMs, registerAutoRefresh]);

  useEffect(() => {
    if (enabled) {
      setRefreshing(isRefreshing);
    }
  }, [enabled, isRefreshing, setRefreshing]);

  return { markRefreshed };
};

export default AutoRefreshProvider;
